import "regenerator-runtime/runtime"
import _ from "lodash"
import { createClient } from "graphql-ws"
import { RelayNetworkLayer, urlMiddleware, loggerMiddleware, retryMiddleware } from "react-relay-network-modern"
import { Environment, RecordSource, Store, Observable } from "relay-runtime"
import { resolveGraphqlApiPath, resolveGraphqlSubscriptionPath } from "./resolveApiPath"

const NODE_ENV = process.env.NODE_ENV

let cachedPayload
let cachedSignedContext
let cachedUrl
let cachedEnvironment

// Function to get the sessionJWT from cookies


// A single ws client shared by every subscription: graphql-ws multiplexes all
// operations over one socket, and one socket is the unit we can health-check
// and restart.
let wsClient = null
let wsConnectionParams = {}
let activeSocket = null
let pongTimeout = null

const getWsClient = () => {
  if (!wsClient) {
    wsClient = createClient({
      // Thunk, not a constant: resolved when the socket is (re)opened in the
      // browser, so the host-based mapping sees the real window.location.host.
      url: () => resolveGraphqlSubscriptionPath(),
      // Thunk, so a reconnect always carries the latest auth.
      connectionParams: () => wsConnectionParams,
      // Mobile networks drop idle connections without a close frame, leaving
      // subscriptions silently dead. The protocol-level ping surfaces that,
      // and the pong timeout below turns it into a close so the retry loop
      // can take over.
      keepAlive: 15000,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      on: {
        connected: (socket) => {
          activeSocket = socket
        },
        ping: (received) => {
          if (!received) {
            pongTimeout = setTimeout(() => {
              if (activeSocket?.readyState === WebSocket.OPEN) {
                activeSocket.close(4408, "Ping timeout")
              }
            }, 10000)
          }
        },
        pong: (received) => {
          if (received) {
            clearTimeout(pongTimeout)
          }
        }
      }
    })
  }
  return wsClient
}

// Abruptly closes the socket; the client then reconnects and replays every
// active subscription. Callers use this to revive a silently dead connection
// (e.g. when the app returns to the foreground or the network comes back).
export const restartWs = () => {
  wsClient?.terminate()
}

const setupSubscription = ({
  operation,
  variables,
  cache,
  payload,
  signedContext,
  session
}) => {
  const nextParams = { payload, signedContext, session }
  if (!_.isEqual(nextParams, wsConnectionParams)) {
    wsConnectionParams = nextParams
    // connectionParams are only sent on connect: force a reconnect so the new
    // auth takes effect. No-op before the first connect.
    restartWs()
  }
  return Observable.create((sink) => {
    return getWsClient().subscribe(
      {
        operationName: operation.name,
        query: operation.text,
        variables,
      },
      sink,
    )
  })
}

export const RelayEnvironment = ({
  payload,
  signedContext,
  session
} = {}, options = {}) => {
  const headers = {}

  // Include the session JWT in the headers for every request

  if (session) {
    headers['Authorization'] = `Bearer ${session}`
  }

  if (!_.isEmpty(payload) && signedContext) {
    headers["X-Woztell-Payload"] = JSON.stringify(payload)
    headers["X-Woztell-SignedContext"] = signedContext
  }
  // Part of the cache key: an environment built during the react-snap
  // prerender (or before the host mapping resolved) must not be reused for the
  // real host.
  const url = resolveGraphqlApiPath()

  if (_.isEqual(payload, cachedPayload) && cachedSignedContext === signedContext && cachedUrl === url && cachedEnvironment) {
    return cachedEnvironment
  }
  cachedPayload = payload
  cachedSignedContext = signedContext
  cachedUrl = url
  const newEnv = new Environment({
    network: new RelayNetworkLayer([
      urlMiddleware({
        // Thunk, so every request re-resolves against the live host.
        url: () => resolveGraphqlApiPath(),
        headers,
        credentials: "include"
      }),
      navigator.userAgent !== "ReactSnap" ? retryMiddleware({
        fetchTimeout: 45000,
        retryDelays: (attempt) => {
          if (attempt > 10) {
            return false
          }
          return Math.pow(2, attempt + 4) * 100
        }
      }) : null,
      NODE_ENV === "production" ? null : loggerMiddleware()
    ], {
      subscribeFn: (operation, variables, cache) => {
        return setupSubscription({ operation, variables, cache, payload, signedContext, session } )
      }
    }),
    store: new Store(new RecordSource()),
  })
  cachedEnvironment = newEnv
  return cachedEnvironment
}
